(() => {
  const root = document.querySelector("[data-storitime-commerce-controls]");
  if (!root || root.dataset.enabled !== "true") return;

  const variantJson = document.querySelector("script[data-storitime-variants]");
  const variants = variantJson ? JSON.parse(variantJson.textContent || "[]") : [];
  const form = root.closest("form") || document.querySelector('form[action*="/cart/add"]');
  const variantSelect = form?.querySelector('select[name="id"], input[name="id"]');
  const dialog = document.querySelector("[data-storitime-notify-dialog]");
  const description = dialog?.querySelector("[data-storitime-notify-description]");
  const status = dialog?.querySelector("[data-storitime-notify-status]");
  const emailInput = dialog?.querySelector("#storitime-notify-email");
  const submitNotify = dialog?.querySelector("[data-storitime-submit-notify]");
  const closeNotify = dialog?.querySelector("[data-storitime-close-notify]");
  const productId = root.dataset.productId;
  const tokenKey = "storitime-commerce-guest-wishlist-token";
  let returnFocus = null;
  let renderVersion = 0;
  let mergedGuestToken = null;
  let activeVariantId = new URLSearchParams(window.location.search).get("variant") || variantSelect?.value || "";

  const request = async (path, method, payload) => {
    const options = { method, headers: { "content-type": "application/json" } };
    if (payload) options.body = JSON.stringify(payload);
    const response = await fetch(`/apps/storitime${path}`, options);
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok) throw new Error(result.error || "request_failed");
    return result;
  };

  const selectedVariant = () => variants.find((variant) => String(variant.id) === String(activeVariantId || variantSelect?.value)) || null;
  const writeGuestToken = (value) => value && localStorage.setItem(tokenKey, value);
  const guestPayload = () => ({ guestToken: localStorage.getItem(tokenKey) || undefined });
  const mergeGuestWishlistOnce = async () => {
    const guestToken = localStorage.getItem(tokenKey);
    if (!guestToken || guestToken === mergedGuestToken) return;
    mergedGuestToken = guestToken;
    try {
      await request("/wishlist", "POST", { action: "merge", guestToken });
    } catch {
      // Guests are expected to receive customer_login_required. A signed Shopify
      // customer triggers the server-resolved merge; no browser owner is trusted.
    }
  };
  const persistedWishlistEntry = async (variant) => {
    await mergeGuestWishlistOnce();
    const result = await request("/wishlist", "POST", { action: "list", ...guestPayload() });
    writeGuestToken(result.guestToken);
    return (result.entries || []).find((entry) =>
      String(entry.shopifyProductId) === String(productId)
      && String(entry.shopifyVariantId || "") === String(variant.id),
    ) || null;
  };

  const render = async () => {
    const thisRender = ++renderVersion;
    const variant = selectedVariant();
    root.replaceChildren();
    if (!variant || !productId) return;
    const notifySlot = document.querySelector("[data-storitime-notify-slot]");
    const soldOutStatus = document.querySelector("[data-storitime-price-soldout]");
    if (notifySlot) notifySlot.replaceChildren();
    if (soldOutStatus) soldOutStatus.hidden = Boolean(variant.available);

    const row = document.createElement("div");
    row.className = "storitime-commerce__row";
    const wishlist = document.createElement("button");
    wishlist.type = "button";
    wishlist.className = "storitime-commerce__action storitime-commerce__wishlist";
    wishlist.setAttribute("aria-pressed", "false");
    wishlist.textContent = "SAVE TO WISH LIST";
    let existingWishlistEntry = null;
    wishlist.addEventListener("click", async () => {
      wishlist.disabled = true;
      try {
        const saved = wishlist.getAttribute("aria-pressed") === "true";
        const result = saved && existingWishlistEntry
          ? await request("/wishlist", "POST", { action: "remove", entryId: existingWishlistEntry.id, ...guestPayload() })
          : await request("/wishlist", "POST", { action: "add", shopifyProductId: String(productId), shopifyVariantId: String(variant.id), shopifyProductHandle: root.dataset.productHandle || "", ...guestPayload() });
        writeGuestToken(result.guestToken);
        existingWishlistEntry = saved ? null : result.entry;
        wishlist.setAttribute("aria-pressed", String(!saved));
        wishlist.textContent = saved ? "SAVE TO WISH LIST" : "SAVED TO WISH LIST";
      } catch { wishlist.textContent = "TRY WISH LIST AGAIN"; }
      finally { wishlist.disabled = false; }
    });
    row.append(wishlist);
    const viewWishlist = document.createElement("button");
    viewWishlist.type = "button";
    viewWishlist.className = "storitime-commerce__action storitime-commerce__wishlist-view";
    viewWishlist.textContent = "VIEW WISH LIST";
    viewWishlist.addEventListener("click", () => {
      document.dispatchEvent(new CustomEvent("storitime:wishlist:open", { detail: { trigger: viewWishlist } }));
    });
    row.append(viewWishlist);

    const createNotifyAction = () => {
      const notify = document.createElement("button");
      notify.type = "button";
      notify.className = "storitime-commerce__action storitime-commerce__notify";
      notify.textContent = "NOTIFY ME";
      notify.setAttribute("aria-label", "Notify me when this selected size returns");
      notify.addEventListener("click", () => {
        returnFocus = notify;
        description.textContent = `${root.dataset.productTitle || "This item"} — ${variant.title}.`;
        status.textContent = "";
        dialog.showModal();
        emailInput.focus();
      });
      return notify;
    };

    // Render the customer-facing controls immediately. Signed endpoint checks
    // still refine their state below, but no empty product-control region is
    // exposed while those requests are in flight.
    let notify = null;
    if (!variant.available) {
      notify = createNotifyAction();
      if (notifySlot) notifySlot.append(notify);
      else row.prepend(notify);
    }
    root.append(row);

    try {
      existingWishlistEntry = await persistedWishlistEntry(variant);
      if (thisRender !== renderVersion) return;
      wishlist.setAttribute("aria-pressed", String(Boolean(existingWishlistEntry)));
      wishlist.textContent = existingWishlistEntry ? "SAVED TO WISH LIST" : "SAVE TO WISH LIST";
    } catch { /* The immediate controls remain usable if wishlist lookup fails. */ }

    if (thisRender !== renderVersion) return;

    try {
      const policyResult = await request(`/preorder?productId=${encodeURIComponent(productId)}&variantId=${encodeURIComponent(variant.id)}`, "GET");
      const verifiedPreorder = policyResult.policy
        && policyResult.policy.enabled
        && policyResult.policy.fullPaymentRequired
        && policyResult.policy.continueSelling
        && String(policyResult.policy.shopifyProductId) === String(productId)
        && String(policyResult.policy.shopifyVariantId) === String(variant.id)
        && Boolean(variant.available);
      if (verifiedPreorder) {
        const card = document.createElement("div");
        card.className = "storitime-commerce__card storitime-commerce__card--pink";
        card.innerHTML = '<img class="storitime-commerce__clock" alt=""><div class="storitime-commerce__copy"><p class="storitime-commerce__eyebrow">STORITIME / PRE-ORDER</p><p class="storitime-commerce__title">PRE-ORDER — PAY IN FULL TODAY</p><p class="storitime-commerce__message"></p></div>';
        card.querySelector(".storitime-commerce__clock").src = root.dataset.clockSrc || "";
        card.querySelector(".storitime-commerce__message").textContent = policyResult.policy.availabilityMessage;
        row.prepend(card);
      } else if (!variant.available && !notify) {
        notify = createNotifyAction();
        if (notifySlot) notifySlot.append(notify);
        else row.prepend(notify);
      }
    } catch { /* Keep customer controls quiet until the Shopify app proxy is configured. */ }
  };

  submitNotify?.addEventListener("click", async () => {
    const variant = selectedVariant();
    if (!variant || !emailInput?.reportValidity()) return;
    submitNotify.disabled = true;
    status.textContent = "Saving your alert…";
    try {
      await request("/restock", "POST", { email: emailInput.value, shopifyProductId: String(productId), shopifyVariantId: String(variant.id), shopifyProductHandle: root.dataset.productHandle || "", consentVersion: "v1" });
      status.textContent = "Alert saved. We’ll email you once this selected size is confirmed available.";
    } catch { status.textContent = "We couldn’t save that alert. Please try again."; }
    finally { submitNotify.disabled = false; }
  });

  dialog?.addEventListener("close", () => returnFocus?.focus());
  closeNotify?.addEventListener("click", () => dialog?.close());
  variantSelect?.addEventListener("change", (event) => {
    activeVariantId = event.target.value;
    render();
  });
  document.addEventListener("variant:change", (event) => {
    activeVariantId = event.detail?.variant?.id || variantSelect?.value || activeVariantId;
    render();
  });
  render();
})();
